/**
 * Taker Flow Chart
 * Stacked bar showing buy (green) / sell (red) taker volume
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassOverview } from '@/hooks/use-coinglass';
import { formatCompact } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { Asset } from '@/stores/alert-store';

interface TakerFlowChartProps {
  asset: Asset;
}

export function TakerFlowChart({ asset }: TakerFlowChartProps) {
  const { data, isLoading } = useCoinglassOverview(asset);

  if (isLoading) return <LoadingSkeleton className="h-64" lines={5} />;

  const taker = data?.aggregatedTakerFlow;
  if (!taker) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Taker Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No data</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = [
    {
      label: 'Taker Volume',
      buy: taker.buyVol,
      sell: taker.sellVol,
    },
  ];

  const ratio = ((taker.ratio ?? 0) * 100).toFixed(1);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Taker Flow</CardTitle>
          <span className="text-xs text-muted-foreground">
            Buy: {ratio}% | Delta: ${formatCompact(taker.delta)}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ left: 10, right: 10 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} />
            <YAxis
              tickFormatter={(v: number) => `$${formatCompact(v)}`}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                `$${formatCompact(value)}`,
                name === 'buy' ? 'Buy' : 'Sell',
              ]}
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px' }}
              labelStyle={{ color: '#d1d5db' }}
              itemStyle={{ color: '#d1d5db' }}
            />
            <Legend
              formatter={(value: string) => (value === 'buy' ? 'Buy' : 'Sell')}
              wrapperStyle={{ fontSize: '11px' }}
            />
            <Bar dataKey="buy" stackId="a" fill="#22c55e" fillOpacity={0.8} radius={[0, 0, 0, 0]} />
            <Bar dataKey="sell" stackId="a" fill="#ef4444" fillOpacity={0.8} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
